import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

// Applies the SEO Manager config (title, meta, Open Graph, Twitter, canonical + JSON-LD)
// to the live document head on every route change. Product pages (/product/:slug) also get
// per-product meta + Product JSON-LD from GET /seo/product/:slug.
function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

function setLd(id, data) {
  let el = document.getElementById(id);
  if (!data) { if (el) el.remove(); return; }
  if (!el) { el = document.createElement("script"); el.id = id; el.type = "application/ld+json"; document.head.appendChild(el); }
  el.text = JSON.stringify(data);
}

export default function SeoHead() {
  const loc = useLocation();
  const [cfg, setCfg] = useState(null);
  const ldDone = useRef(false);

  useEffect(() => { api.get("/seo/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!cfg) return;
    const path = loc.pathname;
    const base = cfg.base_url || "";
    const ov = (cfg.overrides || []).find((o) => o.path === path) || {};
    let title = ov.title || cfg.default_title;
    let desc = ov.description || cfg.default_description;
    let ogImg = ov.og_image || cfg.default_og_image;

    const apply = () => {
      document.title = title;
      setMeta("name", "description", desc);
      setMeta("property", "og:site_name", cfg.site_name);
      setMeta("property", "og:title", title);
      setMeta("property", "og:description", desc);
      setMeta("property", "og:type", "website");
      setMeta("property", "og:url", base + path);
      setMeta("property", "og:image", ogImg);
      setMeta("name", "twitter:card", ogImg ? "summary_large_image" : "summary");
      setMeta("name", "twitter:title", title);
      setMeta("name", "twitter:description", desc);
      if (cfg.twitter_handle) setMeta("name", "twitter:site", cfg.twitter_handle);
      setMeta("name", "robots", ov.noindex ? "noindex,nofollow" : (ov.robots || "index,follow"));
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) { link = document.createElement("link"); link.setAttribute("rel", "canonical"); document.head.appendChild(link); }
      link.setAttribute("href", base + path);
    };

    // Global Organization + WebSite JSON-LD once.
    if (!ldDone.current && cfg.structured_data?.organization) {
      ldDone.current = true;
      setLd("ld-org", { "@context": "https://schema.org", "@type": "Organization", name: cfg.site_name, url: base });
      if (cfg.structured_data?.website) {
        setLd("ld-website", { "@context": "https://schema.org", "@type": "WebSite", name: cfg.site_name, url: base,
          potentialAction: { "@type": "SearchAction", target: `${base}/shop?q={search_term_string}`, "query-input": "required name=search_term_string" } });
      }
    }

    // Per-product SEO on /product/:slug.
    const m = path.match(/^\/product\/([^/]+)$/);
    if (m) {
      api.get(`/seo/product/${m[1]}`).then((r) => {
        const d = r.data;
        title = d.title || title; desc = d.description || desc; ogImg = d.og_image || ogImg;
        apply();
        setLd("ld-product", d.enabled ? d.json_ld : null);
      }).catch(() => { apply(); setLd("ld-product", null); });
    } else {
      setLd("ld-product", null);
      apply();
    }
  }, [cfg, loc.pathname]);

  return null;
}
